import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import { wxAuth } from "@/api/login";
import {
  redirectToWxAuth,
  checkState,
  recordAuthFail,
  shouldRetryAuth,
  getAuthMode,
  setAuthMode,
  saveToken,
  clearToken,
  isTokenExpired,
  markAuthFresh,
  takeAuthReturnRoute,
  rememberAuthReturnRoute,
  markReauthorization,
  consumeReauthorization,
  clearAuthRedirecting,
  consumeAuthHistory,
  getAuthUrl,
  ENABLE_401_REDIRECT,
} from "@/utils/wechat";

const routes: Array<RouteRecordRaw> = [
  { path: "/", redirect: "/home" },
  {
    path: "/auth/callback",
    name: "AuthCallback",
    component: () => import("@/views/auth/callback.vue"),
    meta: { title: "正在登录", public: true },
  },
  {
    path: "/platform/notice",
    name: "PlatformNotice",
    component: () => import("@/views/platform/notice.vue"),
    // 公众页：微信等非企业微信环境打开分享链接时展示，不依赖登录态
    meta: { title: "请在企业微信中打开", public: true },
  },
  {
    path: "/bind",
    name: "Bind",
    component: () => import("@/views/bind/index.vue"),
    meta: { title: "绑定" },
  },
  {
    path: "/bind/switch",
    name: "BindSwitch",
    component: () => import("@/views/bind/switch.vue"),
    meta: { title: "切换" },
  },
  {
    path: "/home",
    name: "Home",
    component: () => import("@/views/home/index.vue"),
    meta: { title: "弘人智汇", keepAlive: true },
  },
  // ===== 小程序迁移页面（适配 H5）=====
  {
    path: "/work",
    name: "Work",
    component: () => import("@/views/work/index.vue"),
    meta: { title: "工作台", keepAlive: true },
  },
  {
    path: "/time/report",
    name: "TimeReport",
    component: () => import("@/views/time/report/index.vue"),
    meta: { title: "考勤明细", keepAlive: true },
  },
  {
    path: "/door/open/list",
    name: "DoorOpenList",
    component: () => import("@/views/door/open/list.vue"),
    meta: { title: "远程开门", keepAlive: true },
  },
  {
    path: "/door/open/info",
    name: "DoorOpenInfo",
    component: () => import("@/views/door/open/info.vue"),
    meta: { title: "开门记录", keepAlive: true },
  },
  {
    path: "/door/visitor/list",
    name: "DoorVisitorList",
    component: () => import("@/views/door/visitor/list.vue"),
    meta: { title: "访客管理", keepAlive: true },
  },
  {
    path: "/door/visitor/detail",
    name: "DoorVisitorDetail",
    component: () => import("@/views/door/visitor/detail.vue"),
    meta: { title: "访客详情" },
  },
  {
    path: "/door/visitor/add",
    name: "DoorVisitorAdd",
    component: () => import("@/views/door/visitor/add.vue"),
    meta: { title: "访客通行" },
  },
  // ===== 会议系统页面（统一挂在 /meet 下）=====
  { path: "/meet", redirect: "/meet/home" },
  {
    path: "/meet/home",
    name: "MeetingHome",
    component: () => import("@/views/meet/home/index.vue"),
    meta: { title: "会议首页", keepAlive: true },
  },
  {
    path: "/meet/reservation",
    name: "Reservation",
    component: () => import("@/views/meet/reservation/index.vue"),
    // 预约表单不缓存：每次进入都应是一份全新表单；
    // 选会议室往返的场景已由页面内的 sessionStorage 草稿负责保留内容
    meta: { title: "预约会议" },
  },
  {
    path: "/meet/my/reservation",
    name: "MyReservation",
    component: () => import("@/views/meet/my/reservation/index.vue"),
    meta: { title: "我的预约", keepAlive: true },
  },
  {
    path: "/meet/my/meeting",
    name: "MyMeeting",
    component: () => import("@/views/meet/my/meeting/index.vue"),
    meta: { title: "我的会议", keepAlive: true },
  },
  {
    path: "/meet/my/reservation/detail",
    name: "MyReservationDetail",
    component: () => import("@/views/meet/my/reservation/detail.vue"),
    meta: { title: "会议详情" },
  },
  {
    path: "/meet/room",
    name: "Room",
    component: () => import("@/views/meet/room/index.vue"),
    meta: { title: "会议室", keepAlive: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || "/"),
  routes,

  // 新开页面时回到顶部；返回/前进时恢复离开前的滚动位置。
  // 列表等页面数据是异步加载的，这里等页面渲染出足够高度后再恢复，
  // 避免页面高度不够导致滚动位置被压回顶部。
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return new Promise((resolve) => {
        const target = savedPosition.top || 0;
        const start = Date.now();

        const check = () => {
          const maxTop =
            document.documentElement.scrollHeight - window.innerHeight;
          // 页面高度足以停到目标位置，或等待超时后即恢复
          if (target <= maxTop || Date.now() - start > 2000) {
            resolve(savedPosition);
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });
    }
    return { top: 0, left: 0 };
  },
});

const normalizeReturnRoute = (route: string | null): string => {
  if (!route) return "/home";
  if (route.startsWith("#/")) return route.slice(1);
  return route.startsWith("/") ? route : `/${route}`;
};

const AUTH_QUERY_KEYS = ["code", "state", "accessToken", "authMode"];

const getUrlQuery = (to: RouteLocationNormalized, key: string) => {
  const fromRoute = to.query[key];
  return typeof fromRoute === "string" && fromRoute ? fromRoute : undefined;
};

const cleanAuthQuery = () => {
  const url = new URL(window.location.href);
  let changed = false;
  AUTH_QUERY_KEYS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState(window.history.state, "", url.toString());
  }
};

const USED_CODE_KEY = "wx_used_code";

router.beforeEach(async (to, _from, next) => {
  document.title = (to.meta.title as string) || "弘人智汇";

  // 支持通过 URL 参数 authMode 临时切换授权模式（写入 sessionStorage，便于联调，不回写 URL）
  const authModeParam = getUrlQuery(to, "authMode");
  if (authModeParam === "frontend" || authModeParam === "backend") {
    setAuthMode(authModeParam);
  }

  // 微信授权回调（仅 frontend 模式）：History URL 携带 code，校验 state 后用 code 换取 token
  // 回调地址固定使用 code；即使旧 sessionStorage 残留 backend 模式，也不能让回调页停留不处理
  const wxCode = getUrlQuery(to, "code");
  if (wxCode) {
    // 授权回调版本标记：用于确认线上静态资源是否已更新
    console.info("[auth-debug-v20260827-02] callback reached", {
      path: window.location.pathname,
      hash: window.location.hash,
      hasCode: true,
      authMode: getAuthMode(),
    });

    // code 一次性消费去重：微信回跳后 clean 阶段若用户按返回落回带 code 的 URL，
    // 直接复用已换取的 token 并硬替换到目标页，避免再次换 token 并产生第二条业务记录
    const usedCode = sessionStorage.getItem(USED_CODE_KEY);
    if (usedCode === wxCode && localStorage.getItem("token")) {
      console.info("[auth-debug] duplicate code consumed, redirect to target", {
        wxCode,
      });
      clearAuthRedirecting();
      const dupIsReauth = consumeReauthorization();
      if (dupIsReauth) takeAuthReturnRoute();
      // 同一个 code 已经换取过 token，不能再次 history.go(-1)，否则企业微信
      // 返回到带 code 的历史条目时会重复后退并形成死循环。
      consumeAuthHistory();
      next({ path: "/home", replace: true });
      return;
    }

    // 授权锁要保持到 code 换 token 完成，避免回调请求期间再次发起授权
    // 校验 state 防止 CSRF；但 state 缺失/不匹配时（收藏夹、历史记录直接进入的回跳链接，
    // sessionStorage 中无对应 state）仍尝试用一次性 code 换取 token，避免触发第二次授权跳转；
    // 换取失败由 recordAuthFail + shouldRetryAuth 退避兜底，不会死循环
    if (!checkState(getUrlQuery(to, "state"))) {
      console.warn("微信授权 state 校验未通过，仍尝试用 code 换取 token");
    }
    let authOk = false;
    try {
      console.info("[auth-debug-v20260827-01] wxAuth start");
      const { data } = await wxAuth({ code: wxCode });
      console.info("[auth-debug-v20260827-01] wxAuth response", {
        hasData: Boolean(data),
        hasAccessToken: Boolean(data?.access_token),
      });
      if (data?.access_token) {
        saveToken(data.access_token, data.expires_in);
        markAuthFresh();
        // 标记 code 已消费，防止返回再次触发换取并产生第二条记录
        sessionStorage.setItem(USED_CODE_KEY, wxCode);
        authOk = true;
      } else {
        recordAuthFail();
      }
    } catch (e) {
      console.error("[auth-debug-v20260827-01] wxAuth failed", e);
      recordAuthFail();
    }
    console.info("[auth-debug-v20260827-02] callback finished", {
      authOk,
      returnRoute: sessionStorage.getItem("wx_auth_return_route"),
    });
    clearAuthRedirecting();
    const isReauthorization = consumeReauthorization();
    const wxReturnRoute = isReauthorization ? takeAuthReturnRoute() : null;
    const targetPath = normalizeReturnRoute(wxReturnRoute);
    if (!authOk) {
      // 失败留在回调页：需清掉 code 避免死循环，但这是唯一的 replace 操作
      cleanAuthQuery();
      next({ path: "/auth/callback", query: { error: "1" }, replace: true });
      return;
    }
    // 成功：原地替换当前回调路由，清除 code，避免回退到企业微信授权页形成循环。
    consumeAuthHistory();
    next({ path: targetPath, replace: true });
    return;
  }

  // 进入每个页面前判断是否已登录；后端授权回跳会携带 accessToken
  const accessToken = getUrlQuery(to, "accessToken");
  let token = accessToken ?? localStorage.getItem("token");
  if (accessToken) {
    clearAuthRedirecting();
    // backend 模式回跳：accessToken 为刚下发的凭证，无有效期信息，不记录过期时间
    saveToken(accessToken);
    markAuthFresh();
    const isReauthorization = consumeReauthorization();
    const tokenReturnRoute = isReauthorization ? takeAuthReturnRoute() : null;
    const tokenTarget = normalizeReturnRoute(tokenReturnRoute);
    // 同 accessToken 分支：由 Router 原子替换当前回调条目，避免产生第二条目标页记录
    next({ path: tokenTarget, replace: true });
    return;
  }
  // 已登录但 token 已过期：标记重新授权并立即发起授权，避免先 next(true) 推入空页面
  // 再 replace 导致栈内出现两份相同业务页（返回需点两次）
  if (token && isTokenExpired()) {
    markReauthorization();
    rememberAuthReturnRoute();
    clearToken();
    token = null;
    if (shouldRetryAuth() && getAuthUrl()) {
      redirectToWxAuth();
    }
    // 关键：拦截当前导航，不产生新的历史记录；授权回跳会 replace 回正确页面
    next(false);
    return;
  }
  // 授权状态页及其他公开路由无需登录；回调失败时必须允许页面正常渲染，避免导航取消后白屏
  if (to.meta.public || ["/student"].includes(to.path)) {
    next();
    return;
  }

  // 访客邀请登记页（被邀请者填写：type 不传或为 0）无需登录态：
  // 提交接口本身免鉴权（isToken:false），若强制走微信授权，回跳会丢失
  // URL 中加密的 t 参数，导致被访人回退成访客本地缓存的信息或为空
  if (to.path === "/door/visitor/add" && String(to.query.type ?? "0") === "0") {
    next();
    return;
  }

  if (!token) {
    // 未登录：尝试发起授权。授权跳转失败或被锁定时也不能放行到业务首页，
    // 否则页面会在没有登录态的情况下直接渲染。
    const redirected = ENABLE_401_REDIRECT && redirectToWxAuth();
    if (redirected) {
      next(false);
    } else {
      next({ path: "/auth/callback", query: { error: "1" }, replace: true });
    }
    return;
  }

  next();
});

export default router;
